// conversation-pagination.ts — pure helpers for the team-conversations infinite scroll.
//
// Extracted from useConversationsSurface (page-advance) and MapListView (dedupe) so both decisions are
// unit-testable: the SPA's SSR-only test harness can't drive scroll / IntersectionObserver, so the
// real regression seam for the paging behavior is these pure functions. See bug-307 (the grow-the-limit
// approach collapsed the list and snapped the scroll to top, and the server's 100-row limit cap walled
// the list at 100) and bug-308 (offset drift on the live recency feed can dup/skip rows).
import type { ConversationsSurfaceData, ConversationListItem } from '../hooks/useConversationsSurface';

// The offset for the NEXT page = rows fetched so far, until we reach the server's (unclamped)
// total_count. Returns undefined to stop paging:
//   - no pages yet → start at offset 0
//   - the last page came back empty → the offset ran past the end (the live feed can shift between
//     fetches), so there is nothing more to load
//   - we have already loaded >= total_count
// Because each page is a fixed small size, offsets keep advancing past 100 — clearing the old wall
// where one ever-growing `limit` was clamped to 100 server-side (bug-307).
export function nextConversationOffset(pages: ConversationsSurfaceData[]): number | undefined {
  if (pages.length === 0) return 0;
  const last = pages[pages.length - 1];
  if (last.conversations.length === 0) return undefined;
  const loaded = pages.reduce((sum, page) => sum + page.conversations.length, 0);
  return loaded < last.total_count ? loaded : undefined;
}

// Flatten the accumulated pages in order, dropping any id already seen. The feed is recency-ordered and
// live, so a row can shift across a page boundary and arrive in two pages; keeping the FIRST occurrence
// avoids duplicate React keys and doubled rows (the bug-308 mitigation).
export function dedupeConversations(pages: ConversationsSurfaceData[]): ConversationListItem[] {
  const seen = new Set<string>();
  const out: ConversationListItem[] = [];
  for (const page of pages) {
    for (const c of page.conversations) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        out.push(c);
      }
    }
  }
  return out;
}
